import { useState } from "react";
import { Warn } from "../components";
import { referenceUrl, WORK_CATEGORIES, type ReferenceGroup } from "../lib/api";
import { sha256Hex } from "../lib/sha256";
import { addressTitle, fmtBytes, fmtDateTime, placeLine } from "../lib/status";

/**
 * The file list for one work-category section (#51).
 *
 * A placeable unit, deliberately: #50 owns the section markup (KPIs,
 * kecamatan breakdown, Database-style headers) and drops one of these into
 * each of its three sections. Built against the #49 work_category shape, so
 * the group, the area links, and the missing rows move across unchanged --
 * only the surrounding section wrapper is #50's to provide.
 *
 * Files group by content hash with area links, like the Database: the same
 * bytes uploaded for two areas is one file linked to two areas. Areas with
 * no file are visibly missing, never silently complete. Supervisors upload
 * per area from the missing row; management (no evidence.open) lists the
 * metadata and is offered no bytes, the same split as the Database page.
 *
 * `bare` renders the list without its own section+h2 wrapper, for nesting
 * inside the owning Kategori section: the section already names the parent
 * with its subs beneath, so a second two-row header would double the
 * headers (six instead of three). In bare mode the list keeps its h3 label
 * so each of the three sections nests exactly one file list, empty or not.
 */
export function WorkCategoryFiles({
  group,
  onOpen,
  canOpen,
  canUpload,
  onChanged,
  bare = false,
}: {
  group: ReferenceGroup;
  onOpen: (areaId: number) => void;
  canOpen: boolean;
  canUpload: boolean;
  onChanged: () => void;
  bare?: boolean;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const subs = WORK_CATEGORIES[group.work_category].join(" · ");

  const upload = async (areaId: number, file: File) => {
    setError(null);
    setBusy(areaId);
    try {
      const sha = await sha256Hex(file);
      const res = await fetch(`/api/areas/${areaId}/reference`, {
        method: "PUT",
        credentials: "same-origin",
        headers: { "content-type": file.type || "application/octet-stream", "x-content-sha256": sha },
        body: file,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `Unggahan gagal (${res.status}).`);
      }
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unggahan gagal.");
    } finally {
      setBusy(null);
    }
  };

  const empty = group.files.length === 0 && group.missing.length === 0;

  const body = (
    <>
      {error && (
        <div className="mb16">
          <Warn tone="red">{error}</Warn>
        </div>
      )}

      {empty ? (
        <div className="panel">
          {bare && <h3>Berkas referensi</h3>}
          <p className="mute small" style={{ margin: 0 }}>
            Belum ada area {group.work_category}.
          </p>
        </div>
      ) : (
        <div className="panel">
          {bare && <h3>Berkas referensi</h3>}
          <table>
            <thead>
              <tr>
                <th>Berkas</th>
                <th>Area</th>
                <th>Diunggah</th>
                <th style={{ textAlign: "right" }}>Berkas</th>
              </tr>
            </thead>
            <tbody>
              {group.files.map((f) => (
                <tr key={f.reference_id}>
                  <td>
                    <span className="chip chip-granted">Berkas referensi</span>{" "}
                    <span className="small mute mono">{fmtBytes(f.byte_size)}</span>
                  </td>
                  <td>
                    {f.areas.map((a) => (
                      <div key={a.area_id}>
                        <button className="btn btn-sm btn-ghost" onClick={() => onOpen(a.area_id)}>
                          {addressTitle(a)}
                        </button>{" "}
                        <span className="small mute">{placeLine(a)}</span>
                        {a.work_subcategory && (
                          <span className="small mute"> · {a.work_subcategory}</span>
                        )}
                      </div>
                    ))}
                  </td>
                  <td className="small mute" title={fmtDateTime(f.created_at)}>
                    {f.author ?? "—"}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {canOpen ? (
                      <a
                        className="btn btn-sm"
                        href={referenceUrl(f.reference_id)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Unduh
                      </a>
                    ) : (
                      <span className="small mute" title="Akses bukti tidak tersedia">
                        🔒 terkunci
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {group.missing.map((m) => (
                <tr key={`missing-${m.area_id}`}>
                  <td>
                    <span className="chip chip-pending">Belum ada berkas</span>{" "}
                    <span className="small mute">berkas referensi belum diunggah</span>
                  </td>
                  <td>
                    <button className="btn btn-sm btn-ghost" onClick={() => onOpen(m.area_id)}>
                      {addressTitle(m)}
                    </button>{" "}
                    <span className="small mute">{placeLine(m)}</span>
                    {m.work_subcategory && (
                      <span className="small mute"> · {m.work_subcategory}</span>
                    )}
                  </td>
                  <td className="small mute">—</td>
                  <td style={{ textAlign: "right" }}>
                    {canUpload ? (
                      <label className="btn btn-sm" style={{ cursor: busy === m.area_id ? "wait" : "pointer" }}>
                        {busy === m.area_id ? "Mengunggah…" : "Unggah berkas"}
                        <input
                          type="file"
                          hidden
                          accept="application/pdf,image/jpeg,image/png"
                          disabled={busy !== null}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            e.target.value = "";
                            if (file) void upload(m.area_id, file);
                          }}
                        />
                      </label>
                    ) : (
                      <span className="small mute">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  if (bare) return body;

  return (
    <section aria-label={`Referensi ${group.work_category}`}>
      <h2 className="page-sub" style={{ fontSize: 16, margin: "20px 0 8px", lineHeight: 1.35 }}>
        {group.work_category}
        <br />
        <span className="small mute">{subs}</span>
      </h2>
      {body}
    </section>
  );
}
